import { useUserStore } from '@/stores/userStore';
import { apiClient } from '@/lib/apiClient';
import { appliquerLangue, useT, i18next, type Langue } from '@/lib/i18n';

/**
 * LangToggle — le sélecteur de langue de l'interface (`#796` / `#797`).
 *
 * ⚠ **MÊME MARKUP QUE `ChannelToggle`** (Retail/Wholesale) — pilule segmentée,
 *   actif `bg-brand-samsung-500`. Un vrai toggle, pas un bouton bascule (choix
 *   Hkm) : chaque langue est un bouton, l'active est pleine.
 *
 * ⚠ La langue est une propriété du **compte** : le clic (1) bascule l'écran
 *   immédiatement (`appliquerLangue`), (2) met à jour le store, (3) persiste via
 *   `PATCH /auth/me/language`. Au prochain `/auth/me`, la langue du compte
 *   re-pilote l'interface (`useAuth.fetchMe`).
 */
export function LangToggle() {
  const user = useUserStore((s) => s.user);
  const setUser = useUserStore((s) => s.setUser);
  const t = useT();

  const courante: Langue =
    user?.language === 'en' ? 'en'
    : user?.language === 'fr' ? 'fr'
    : i18next.language === 'en' ? 'en' : 'fr';

  const choisir = (cible: Langue) => {
    if (cible === courante) return;
    appliquerLangue(cible);
    if (user) setUser({ ...user, language: cible });
    void apiClient.patch('/auth/me/language', { language: cible });
  };

  const OPTIONS: Array<{ value: Langue; label: string }> = [
    { value: 'fr', label: t('langue.fr') },
    { value: 'en', label: t('langue.en') },
  ];

  return (
    <div
      role="group"
      aria-label={t('langue.titre')}
      data-testid="lang-toggle"
      className="flex items-center gap-1 rounded-full border border-surface-dark-border bg-surface-dark p-0.5 shrink-0"
    >
      {OPTIONS.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => choisir(o.value)}
          aria-pressed={courante === o.value}
          data-testid={`lang-${o.value}`}
          className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium transition-colors ${
            courante === o.value
              ? 'bg-brand-samsung-500 text-white'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
